import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable custom/no-helper-functions-in-component-file -- app bootstrap entry, not a component file: hosts the root wiring factories */ import { Router } from 'react-router-dom';
import DateFnsUtils from '@date-io/date-fns';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import * as loom from '@loomhq/loom-embed';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as Sentry from '@sentry/react';
import seon from '@seontechnologies/seon-javascript-sdk';
import { createBrowserHistory } from 'history';
import isMobileDevice from 'is-mobile';
import Persona from 'persona';
import Pusher from 'pusher-js';
import { createRoot } from 'react-dom/client';
import { usePlaidLink } from 'react-plaid-link';
import { GoogleButton, MuiPickersUtilsProvider, onlyShowFocusOnTabs, prefetchLottie } from 'seagull';
import { IoContextProvider } from '~/contexts';
import { PusherProvider } from '~/pusher';
import { AppWithEventReporter } from './App';
import { uploadFileToCloudStorage } from './infra/utils';
import { initSentry } from './initSentry';
import { makeIntercomClient, RealIntercomClient } from './intercom/intercomClient';
import { getNativePlatform, isNativePlatform } from './is-native';
import { NativeBootstrapRetryScreen } from './NativeBootstrapRetryScreen';
import { makeNativeChangeLocation, makeNativeLocationReload } from './providers/browser-navigation';
import { RealCookiesManager } from './providers/CookiesManager';
import { RootDeviceProvider } from './providers/DeviceProvider';
import { mockNativeBridge } from './providers/IoContext';
import { LazyReactPlayer, LazyYoutubePlayer, prefetchPlayers } from './providers/lazyPlayers';
import { MobileViewportWidthProvider } from './providers/MobileViewportWidthProvider';
import { makeNativeOpenNewTab, makeOpenNewTab } from './providers/openNewTab';
import { initStoreApi } from './store/store-api';
import { StoreProvider } from './store/StoreProvider';
import { actualReactStripeJs } from './stripe/reactStripeJs';
import { actualDownloadCsv } from './utils/csv.utils';
import { getTimezoneAbbreviation } from './utils/date-timezone.utils';
import { initializeLogBIEvent, sendBiViaBeacon } from './utils/logBIEvent/logBIEvent';
import { logBIEventOld } from './utils/logBIEventOld';
const history = createBrowserHistory();
const getPusher = ()=>{
    const pusher = new Pusher(window.pusherAppKey, {
        cluster: 'us2'
    });
    return {
        subscribe: (channelName)=>{
            return pusher.subscribe(channelName);
        },
        unsubscribe: (channelName)=>{
            return pusher.unsubscribe(channelName);
        },
        channel: (channelName)=>{
            return pusher.channel(channelName);
        }
    };
};
const getHotjar = ()=>({
        identify (userId, payload) {
            window.hj && window.hj('identify', userId, payload);
        }
    });
const loomParser = (url)=>loom.oembed(url);
const makePersonaClient = (param)=>{
    let { onError, onReady, onComplete, onCancel } = param;
    return {
        open (inquiryId, sessionToken) {
            const client = new Persona.Client({
                inquiryId,
                sessionToken: sessionToken || undefined,
                onError,
                onReady: ()=>{
                    onReady && onReady();
                    client.open();
                },
                onComplete,
                onCancel
            });
        }
    };
};
function injectScriptOrLink(script) {
    return new Promise((resolve, reject)=>{
        if (script.id) {
            const existingScript = document.getElementById(script.id);
            if (existingScript) {
                return resolve(null);
            }
        }
        script.onload = ()=>resolve(null);
        script.onerror = ()=>reject();
        document.head.appendChild(script);
    });
}
const MOUNT_NODE_ID = 'main';
function renderRetryScreen(error) {
    // eslint-disable-next-line no-console
    console.error('[native-bootstrap] failed to load /config:', error);
    void import('./native').then((nativeModule)=>nativeModule.hideSplashScreen());
    const root = createRoot(document.getElementById(MOUNT_NODE_ID));
    root.render(/*#__PURE__*/ _jsx(NativeBootstrapRetryScreen, {
        onRetry: ()=>window.location.reload()
    }));
}
const noopNativeAuthTokenStore = {
    persist: ()=>{},
    clear: ()=>{}
};
function prefetchHeavyMediaOnIdle() {
    const prefetch = ()=>{
        prefetchLottie();
        prefetchPlayers();
    };
    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(prefetch);
    } else {
        window.setTimeout(prefetch, 2000);
    }
}
function mountReact(nativeBridge, nativeAuthTokenStore, nativeIntercom) {
    var _window_googleIosClientId;
    var _window___anchorNativeBootResolved, _window;
    initSentry(history);
    onlyShowFocusOnTabs();
    try {
        seon.init();
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                source: 'seon-init'
            }
        });
    }
    const gtmParams = {
        id: window._gtmId
    };
    // eslint-disable-next-line no-restricted-syntax -- root composition: defines the production getNow clock seam (frontend-code.md)
    const getNow = ()=>new Date();
    const root = createRoot(document.getElementById(MOUNT_NODE_ID));
    root.render(/*#__PURE__*/ _jsx(Router, {
        history: history,
        children: /*#__PURE__*/ _jsx(IoContextProvider, {
            getNow: getNow,
            usePlaidLink: usePlaidLink,
            intercomClient: makeIntercomClient(new RealIntercomClient(), nativeIntercom),
            getHotjar: getHotjar,
            personaClient: makePersonaClient,
            ReactPlayer: LazyReactPlayer,
            YoutubePlayer: LazyYoutubePlayer,
            GoogleButton: GoogleButton,
            loomParser: loomParser,
            cookies: new RealCookiesManager(window._authDomain, window.localStorage, nativeAuthTokenStore),
            logBIEventOld: logBIEventOld,
            logBIEvent: initializeLogBIEvent(sendBiViaBeacon),
            localStorage: window.localStorage,
            reactStripe: actualReactStripeJs,
            getTimezoneAbbreviation: getTimezoneAbbreviation,
            browser: {
                locationReload: nativeBridge.isNative ? makeNativeLocationReload({
                    reload: ()=>window.location.reload(),
                    showSplashScreen: nativeBridge.showSplashScreen
                }) : ()=>window.location.reload(),
                changeLocation: nativeBridge.isNative ? makeNativeChangeLocation({
                    navigate: (url)=>window.location.href = url,
                    showSplashScreen: nativeBridge.showSplashScreen,
                    getLocation: ()=>({
                            href: window.location.href,
                            origin: window.location.origin
                        })
                }) : (url)=>window.location.href = url,
                getOrigin: ()=>window.location.origin,
                copyToClipboard: (text)=>navigator.clipboard.writeText(text),
                openNewTab: nativeBridge.isNative ? makeNativeOpenNewTab(nativeBridge.openExternalUrl) : makeOpenNewTab(function() {
                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                        args[_key] = arguments[_key];
                    }
                    return window.open(...args);
                }),
                downloadCsv: actualDownloadCsv,
                injectScriptOrLink,
                uploadFileToCloudStorage,
                scrollIntoView: function scrollIntoView(element) {
                    let options = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
                    return element.scrollIntoView && element.scrollIntoView({
                        behavior: options.disableScrollAnimation ? 'instant' : 'smooth',
                        block: options.block || 'center'
                    });
                },
                scrubUrlFragment: ()=>window.history.replaceState(null, '', window.location.pathname + window.location.search)
            },
            native: nativeBridge,
            children: /*#__PURE__*/ _jsx(StoreProvider, {
                apiHost: window._apiHostname,
                mgmtHost: window._mgmtHostname,
                pdfHost: window._pdfHostname,
                apiGatewayHost: window._apiGatewayHostname,
                authDomain: window._authDomain,
                enableAppcues: window.enableAppcues,
                appcuesAccountId: window.appcuesAccountId,
                useAuthorizationHeader: window.useAuthorizationHeader,
                googleIosClientId: (_window_googleIosClientId = window.googleIosClientId) !== null && _window_googleIosClientId !== void 0 ? _window_googleIosClientId : '',
                sessionTimeoutMS: window.sessionTimeoutMS,
                initStoreApi: initStoreApi,
                children: /*#__PURE__*/ _jsx(RootDeviceProvider, {
                    isMobileDevice: isMobileDevice(),
                    children: /*#__PURE__*/ _jsx(MuiPickersUtilsProvider, {
                        utils: DateFnsUtils,
                        children: /*#__PURE__*/ _jsx(LocalizationProvider, {
                            dateAdapter: AdapterDateFns,
                            children: /*#__PURE__*/ _jsx(GTMProvider, {
                                state: gtmParams,
                                children: /*#__PURE__*/ _jsx(PusherProvider, {
                                    pusher: getPusher(),
                                    children: /*#__PURE__*/ _jsx(MobileViewportWidthProvider, {
                                        children: /*#__PURE__*/ _jsx(AppWithEventReporter, {})
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
    }));
    (_window___anchorNativeBootResolved = (_window = window).__anchorNativeBootResolved) === null || _window___anchorNativeBootResolved === void 0 ? void 0 : _window___anchorNativeBootResolved.call(_window);
    prefetchHeavyMediaOnIdle();
}
const start = async ()=>{
    if (!isNativePlatform()) {
        mountReact(mockNativeBridge, noopNativeAuthTokenStore);
        return;
    }
    document.body.classList.add("capacitor-platform-".concat(getNativePlatform()));
    try {
        const { nativeBootstrap, makeNativeBridge, makeNativeIntercom } = await import('./native');
        const config = await nativeBootstrap.resolveConfig();
        nativeBootstrap.applyRuntimeConfig(nativeBootstrap.applyNativeOverrides(config));
        await nativeBootstrap.hydrateNativeAuthTokenFromPreferences();
        const nativeAuthTokenStore = nativeBootstrap.makeNativeAuthTokenStore();
        const nativeBridge = await makeNativeBridge({
            navigate: (path)=>history.push(path)
        });
        mountReact(nativeBridge, nativeAuthTokenStore, makeNativeIntercom());
    } catch (err) {
        // Catch-all (BootstrapNetworkError, ChunkLoadError on broken OTA, etc.) → retry screen; Capgo rolls back next launch.
        renderRetryScreen(err);
    }
};
void start();
